import React, { useState, useEffect } from 'react';
import { Button, Card, Alert, Spinner, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation, faCircleCheck } from '@fortawesome/pro-regular-svg-icons';
import {
  deleteAllLessons,
  deleteAllStudents,
  fullReset,
  getDataSummary
} from '../../utils/cleanupFirestore';

function CleanupData() {
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadSummary();
  }, []);

  const loadSummary = async () => {
    try {
      const data = await getDataSummary();
      setSummary(data);
    } catch (error) {
      console.error('Error loading summary:', error);
    }
  };

  const handleDeleteLessons = async () => {
    if (!window.confirm('Delete ALL lessons? This cannot be undone!')) {
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const count = await deleteAllLessons();
      setMessage({ type: 'success', text: `Deleted ${count} lessons` });
      await loadSummary();
    } catch (error) {
      setMessage({ type: 'danger', text: `Error: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStudents = async () => {
    if (!window.confirm('Delete ALL students? This cannot be undone!')) {
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const count = await deleteAllStudents();
      setMessage({ type: 'success', text: `Deleted ${count} students` });
      await loadSummary();
    } catch (error) {
      setMessage({ type: 'danger', text: `Error: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const handleFullReset = async () => {
    if (!window.confirm(
      'FULL RESET\n\n' +
      'This will delete ALL students AND lessons!\n\n' +
      'This action CANNOT be undone!\n\n' +
      'Are you absolutely sure?'
    )) {
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const result = await fullReset();
      setMessage({
        type: 'success',
        text: `Full reset complete! Deleted ${result.studentsDeleted} students and ${result.lessonsDeleted} lessons`
      });
      await loadSummary();
    } catch (error) {
      setMessage({ type: 'danger', text: `Error: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cleanup-data-container">
      <Card className="shadow-sm">
        <Card.Header className="bg-danger text-white">
          <h5 className="mb-0"><FontAwesomeIcon icon={faTriangleExclamation} className="me-2" />Data Cleanup Utility</h5>
        </Card.Header>
        <Card.Body>
          <Alert variant="warning">
            <strong>Warning:</strong> These actions permanently delete data and cannot be undone.
            Use before a fresh import if you want to start clean.
          </Alert>

          {message && (
            <Alert variant={message.type} dismissible onClose={() => setMessage(null)}>
              {message.type === 'success' && (
                <FontAwesomeIcon icon={faCircleCheck} className="me-1" />
              )}
              {message.text}
            </Alert>
          )}

          {summary && (
            <div className="mb-4 p-3 bg-light rounded">
              <h6>Current Data:</h6>
              <div className="d-flex gap-3">
                <div>
                  <Badge bg="primary" className="me-2">Students</Badge>
                  <strong>{summary.students}</strong>
                </div>
                <div>
                  <Badge bg="info" className="me-2">Lessons</Badge>
                  <strong>{summary.lessons}</strong>
                </div>
              </div>
              <Button
                variant="link"
                size="sm"
                onClick={loadSummary}
                className="p-0 mt-2"
              >
                Refresh counts
              </Button>
            </div>
          )}

          <div className="d-flex flex-column gap-3">
            <div>
              <h6>Delete Lessons Only</h6>
              <p className="small text-muted">Keeps students, deletes all lessons</p>
              <Button
                variant="warning"
                onClick={handleDeleteLessons}
                disabled={loading || !summary || summary.lessons === 0}
              >
                {loading ? <Spinner size="sm" /> : 'Delete All Lessons'}
              </Button>
            </div>

            <div>
              <h6>Delete Students Only</h6>
              <p className="small text-muted">Keeps lessons, deletes all students</p>
              <Button
                variant="warning"
                onClick={handleDeleteStudents}
                disabled={loading || !summary || summary.students === 0}
              >
                {loading ? <Spinner size="sm" /> : 'Delete All Students'}
              </Button>
            </div>

            <div className="border-top pt-3">
              <h6 className="text-danger">Full Reset</h6>
              <p className="small text-muted">Deletes EVERYTHING - students and lessons</p>
              <Button
                variant="danger"
                onClick={handleFullReset}
                disabled={loading || !summary || (summary.students === 0 && summary.lessons === 0)}
              >
                {loading ? <Spinner size="sm" /> : <><FontAwesomeIcon icon={faTriangleExclamation} className="me-1" />Delete Everything</>}
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}

export default CleanupData;
